// @ts-check
// Novo modelo de WhatsApp de campanha (spec/whatsapp.md seção 3.7 B; contrato 09 de campanhas de WhatsApp, seção 2):
// página inteira com o formulário e a prévia fixa ao lado (embaixo no celular). Número com conta do WhatsApp Business,
// nome amigável que gera o nome na Meta, tipo em dois cartões (Marketing e Comunicado, com o aviso de reclassificação),
// cabeçalho, texto com contador e "Inserir variável", rodapé, botão "Parar de receber" no marketing e um exemplo por
// variável. Os erros de validateTemplateDraft aparecem ao lado do campo, ao sair dele e ao enviar. Todo modelo criado
// aqui tem uso CAMPAIGN; depois de enviar, a página do modelo acompanha a análise da Meta.
import { html } from 'htm/preact';
import { useEffect, useRef, useState } from 'preact/hooks';
import { navigate, searchParamsState } from '../../app/router.js';
import { hasCurrentPermissionFlag } from '../../data/metadata-store.js';
import { listChannels } from '../../data/conversations.js';
import { createWhatsappTemplate } from '../../data/integrations.js';
import { ApiError } from '../../data/api-client.js';
import { PageCard, PageHeader } from '../page/page-card.js';
import { Button } from '../../ui/button.js';
import { Icon } from '../../ui/icon.js';
import { Dropdown, DropdownMenuItems, DropdownMenuSectionLabel, DropdownMenuSeparator, MenuItem } from '../../ui/dropdown.js';
import { Skeleton } from '../../ui/feedback.js';
import { enqueueSnackbar } from '../../ui/snackbar.js';
import { FormError, describeError } from '../settings/integration-ui.js';
import { nextVariableToken } from '../settings/whatsapp-templates/create-template-modal.js';
import { WhatsappMessagePreview } from '../whatsapp-campaigns/whatsapp-message-preview.js';
import {
  CAMPAIGN_WHATSAPP_TEMPLATES_PATH, CAMPAIGN_WHATSAPP_TEMPLATE_BASE_PATH, campaignTemplateHref, channelLabel, insertVariableToken,
} from '../../lib/whatsapp-templates.js';
import { readReturnPath } from '../../lib/return-path.js';
import {
  TEMPLATE_LIMITS, technicalTemplateName, templateTextVariables, validateTemplateDraft,
} from '../../../../shared/whatsapp/template-rules.js';
import { hasTranslation, t } from '../../i18n/index.js';
import { spaLinkHandler } from './whatsapp-templates-tab.js';

/** @typedef {import('../../data/conversations.js').Channel} Channel */

const TEMPLATE_LANGUAGE = 'pt_BR';
/** Variáveis com nome sugeridas no menu, na ordem; rótulo e exemplo em whatsappTemplates.campaignForm. */
export const SUGGESTED_NAMED_VARIABLES = Object.freeze(['nome', 'aluno', 'escola', 'consultor', 'data_visita']);

/**
 * Texto de um erro do rascunho (validateTemplateDraft) ou da API.
 * @param {string} reason
 */
const reasonText = (reason) => {
  const key = `whatsappTemplates.campaignForm.reasons.${reason}`;
  return hasTranslation(key) ? t(key) : reason;
};

/**
 * Recusa da API: motivo do rascunho no campo, código da Meta com texto próprio ou a mensagem genérica.
 * @param {unknown} error
 * @returns {{ field: string | null, message: string }}
 */
export function describeTemplateCreationError(error) {
  if (error instanceof ApiError) {
    const reason = error.details?.reason;
    if (typeof reason === 'string' && hasTranslation(`whatsappTemplates.campaignForm.reasons.${reason}`)) {
      return { field: typeof error.details?.field === 'string' ? error.details.field : null, message: reasonText(reason) };
    }
    if (error.code === 'TEMPLATE_REJECTED') {
      const metaCode = error.details?.metaCode;
      const key = `whatsappTemplates.campaignForm.metaErrors.${metaCode}`;
      return { field: null, message: metaCode && hasTranslation(key) ? t(key) : t('whatsappTemplates.campaignForm.metaErrors.other', { message: error.messages[0] ?? '' }) };
    }
  }
  return { field: null, message: describeError(error) };
}

/** @param {{ messages: string[] }} props */
function FieldErrors({ messages }) {
  if (messages.length === 0) return null;
  return html`${messages.map((message) => html`<p key=${message} class="wa-field-error" role="alert"><${Icon} name="IconAlertTriangle" size=${14} /><span>${message}</span></p>`)}`;
}

export function WhatsappTemplateFormPage() {
  const canManage = hasCurrentPermissionFlag('WORKSPACE') || hasCurrentPermissionFlag('WHATSAPP_CAMPAIGNS');
  const parameters = searchParamsState.value;
  const [returnPath] = useState(() => readReturnPath(parameters.get('from'), `${CAMPAIGN_WHATSAPP_TEMPLATE_BASE_PATH}/new`));
  const backHref = returnPath ?? CAMPAIGN_WHATSAPP_TEMPLATES_PATH;
  const [channels, setChannels] = useState(/** @type {Channel[] | null} */ (null));
  const [loadError, setLoadError] = useState(/** @type {string | null} */ (null));
  const [channelId, setChannelId] = useState('');
  const [friendlyName, setFriendlyName] = useState('');
  const [technicalName, setTechnicalName] = useState('');
  const [isNameEdited, setIsNameEdited] = useState(false);
  const [kind, setKind] = useState(/** @type {'MARKETING' | 'ANNOUNCEMENT'} */ (parameters.get('kind') === 'ANNOUNCEMENT' ? 'ANNOUNCEMENT' : 'MARKETING'));
  const [headerText, setHeaderText] = useState('');
  const [bodyText, setBodyText] = useState('');
  const [footerText, setFooterText] = useState('');
  const [optOutButton, setOptOutButton] = useState(true);
  const [optOutButtonText, setOptOutButtonText] = useState(() => t('whatsappTemplates.campaignForm.optOutButtonDefault'));
  const [examples, setExamples] = useState(/** @type {Record<string, string>} */ ({}));
  const [touched, setTouched] = useState(/** @type {Record<string, boolean>} */ ({}));
  const [isSubmitAttempted, setIsSubmitAttempted] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [serverError, setServerError] = useState(/** @type {{ field: string | null, message: string } | null} */ (null));
  const [isVariableMenuOpen, setIsVariableMenuOpen] = useState(false);
  const bodyRef = useRef(/** @type {HTMLTextAreaElement | null} */ (null));
  const variableButtonRef = useRef(/** @type {HTMLButtonElement | null} */ (null));

  const load = () => {
    setLoadError(null);
    listChannels()
      .then((data) => {
        const withWaba = data.channels.filter((channel) => channel.type === 'WHATSAPP' && channel.settings?.wabaId);
        setChannels(withWaba);
        const requested = parameters.get('channelId');
        setChannelId(withWaba.some((channel) => channel.id === requested) ? /** @type {string} */ (requested) : withWaba[0]?.id ?? '');
      })
      .catch((error) => setLoadError(describeError(error)));
  };
  useEffect(() => {
    document.title = `${t('whatsappTemplates.page.newTitle')} - ${t('campaignsBase.screens.templates')}`;
    load();
  }, []);

  const category = kind === 'MARKETING' ? 'MARKETING' : 'UTILITY';
  const includesOptOut = kind === 'MARKETING' && optOutButton;
  const variables = templateTextVariables(bodyText);
  const draft = {
    name: technicalName, category, headerText, bodyText, footerText,
    examples: variables.map((variable) => examples[variable] ?? ''), optOutButton: includesOptOut, optOutButtonText,
  };
  const problems = validateTemplateDraft(draft);
  /** @param {string} field */
  const messagesFor = (field) => {
    const shown = isSubmitAttempted || touched[field];
    const messages = shown ? problems.filter((problem) => problem.field === field).map((problem) => reasonText(problem.reason)) : [];
    if (serverError?.field === field) messages.push(serverError.message);
    return [...new Set(messages)];
  };
  const touch = (/** @type {string} */ field) => () => setTouched((current) => (current[field] ? current : { ...current, [field]: true }));
  const hasNumbered = variables.some((variable) => /^\d+$/.test(variable));
  const hasNamed = variables.some((variable) => !/^\d+$/.test(variable));

  const onFriendlyName = (/** @type {string} */ value) => {
    setFriendlyName(value);
    if (!isNameEdited) setTechnicalName(technicalTemplateName(value));
  };

  /** @param {string} token @param {string | null} exampleKey */
  const insertToken = (token, exampleKey) => {
    setIsVariableMenuOpen(false);
    const element = bodyRef.current;
    const start = element?.selectionStart ?? bodyText.length;
    const end = element?.selectionEnd ?? start;
    const next = insertVariableToken(bodyText, start, end, token);
    setBodyText(next.text);
    const variable = token.slice(2, -2);
    if (exampleKey && !examples[variable]) setExamples({ ...examples, [variable]: t(exampleKey) });
    requestAnimationFrame(() => {
      element?.focus();
      element?.setSelectionRange(next.cursor, next.cursor);
    });
  };

  const submit = async (/** @type {Event} */ event) => {
    event.preventDefault();
    setIsSubmitAttempted(true);
    setServerError(null);
    if (problems.length > 0 || !channelId) return;
    setIsSaving(true);
    try {
      const template = await createWhatsappTemplate({
        channelId, name: technicalName.trim(), language: TEMPLATE_LANGUAGE, category, usage: 'CAMPAIGN', headerText: headerText.trim(), bodyText: bodyText.trim(),
        footerText: footerText.trim(), examples: draft.examples, optOutButton: includesOptOut, ...(includesOptOut ? { optOutButtonText: optOutButtonText.trim() } : {}),
      });
      enqueueSnackbar({
        message: t('whatsappTemplates.campaignForm.created'), variant: 'success',
        ...(returnPath ? { buttonLabel: t('whatsappTemplates.campaignForm.goBack'), onButtonClick: () => navigate(returnPath) } : {}),
      });
      navigate(`${campaignTemplateHref(template.id)}${returnPath ? `?${new URLSearchParams({ from: returnPath })}` : ''}`);
    } catch (error) {
      setServerError(describeTemplateCreationError(error));
    } finally {
      setIsSaving(false);
    }
  };

  const header = html`<${PageHeader} icon="IconBrandWhatsapp" title=${t('whatsappTemplates.page.newTitle')} />`;
  const backLink = html`<a class="integration-link wa-template-back" href=${backHref} onClick=${spaLinkHandler(backHref)}>
    <${Icon} name="IconArrowLeft" size=${14} />${returnPath && returnPath !== CAMPAIGN_WHATSAPP_TEMPLATES_PATH ? t('whatsappTemplates.campaignForm.goBack') : t('whatsappTemplates.page.back')}
  </a>`;

  if (!canManage) {
    return html`<${PageCard} className="campaigns-screen" header=${header}>
      <div class="wa-template-screen wa-template-page-main">${backLink}<p class="channel-card-note"><${Icon} name="IconLock" size=${14} /><span>${t('whatsappTemplates.campaignTab.readOnlyNote')}</span></p></div>
    <//>`;
  }
  if (channels === null) {
    return html`<${PageCard} className="campaigns-screen" header=${header}>
      <div class="wa-template-screen wa-template-page-main">
        ${backLink}
        ${loadError
          ? html`<div class="integration-empty templates-empty" role="alert"><p>${loadError}</p>
              <${Button} title=${t('whatsappTemplates.actions.retry')} icon="IconRefresh" variant="secondary" size="small" onClick=${load} /></div>`
          : html`<div class="templates-skeleton" aria-busy="true"><${Skeleton} height=${44} radius=${8} /><${Skeleton} height=${120} radius=${8} /><${Skeleton} height=${200} radius=${8} /></div>`}
      </div>
    <//>`;
  }
  if (channels.length === 0) {
    return html`<${PageCard} className="campaigns-screen" header=${header}>
      <div class="wa-template-screen wa-template-page-main">
        ${backLink}
        <div class="integration-empty templates-empty"><p>${t('whatsappTemplates.campaignForm.noNumber')}</p>
          ${hasCurrentPermissionFlag('WORKSPACE')
            ? html`<${Button} title=${t('whatsappTemplates.actions.goToChannels')} icon="IconPlugConnected" variant="secondary" size="small" onClick=${() => navigate('/settings/channels')} />`
            : html`<p>${t('whatsappTemplates.campaignTab.noChannelMember')}</p>`}
        </div>
      </div>
    <//>`;
  }

  const bodyLength = bodyText.trim().length;
  return html`<${PageCard} className="campaigns-screen" header=${header}>
    <div class="wa-template-screen wa-template-page">
      <form class="wa-template-page-main wa-template-form" onSubmit=${submit} noValidate>
        ${backLink}
        ${channels.length > 1
          ? html`<label class="form-field">
              <span class="form-field-label">${t('whatsappTemplates.campaignForm.number')}</span>
              <select class="text-input integration-select" value=${channelId}
                onChange=${(/** @type {Event} */ event) => setChannelId(/** @type {HTMLSelectElement} */ (event.currentTarget).value)}>
                ${channels.map((channel) => html`<option key=${channel.id} value=${channel.id} selected=${channel.id === channelId}>
                  ${channelLabel(channel)}${channel.settings?.verifiedName ? ` (${channel.settings.verifiedName})` : ''}
                </option>`)}
              </select>
              <span class="input-hint">${t('whatsappTemplates.campaignForm.numberHint')}</span>
            </label>`
          : null}
        <label class="form-field">
          <span class="form-field-label">${t('whatsappTemplates.campaignForm.friendlyName')}<span class="form-field-required">*</span></span>
          <input class="text-input" value=${friendlyName} maxLength=${TEMPLATE_LIMITS.maximumNameLength} placeholder=${t('whatsappTemplates.campaignForm.friendlyNamePlaceholder')}
            autocomplete="off" onInput=${(/** @type {Event} */ event) => onFriendlyName(/** @type {HTMLInputElement} */ (event.currentTarget).value)} onBlur=${touch('name')} />
        </label>
        <label class="form-field wa-technical-name">
          <span class="form-field-label">${t('whatsappTemplates.campaignForm.technicalName')}</span>
          <input class="text-input" value=${technicalName} maxLength=${TEMPLATE_LIMITS.maximumNameLength} autocomplete="off" spellcheck=${false}
            aria-invalid=${messagesFor('name').length > 0 ? 'true' : undefined}
            onInput=${(/** @type {Event} */ event) => {
              setIsNameEdited(true);
              setTechnicalName(/** @type {HTMLInputElement} */ (event.currentTarget).value.toLowerCase());
            }} onBlur=${touch('name')} />
          <span class="input-hint">${t('whatsappTemplates.campaignForm.technicalNameHint')}</span>
        </label>
        <${FieldErrors} messages=${messagesFor('name')} />
        <fieldset>
          <legend>${t('whatsappTemplates.campaignForm.kind')}</legend>
          <div class="wa-kind-cards">
            ${/** @type {const} */ (['MARKETING', 'ANNOUNCEMENT']).map((value) => html`<label key=${value} class="wa-kind-card" data-selected=${kind === value ? 'true' : 'false'}>
              <input type="radio" name="wa-template-kind" value=${value} checked=${kind === value} onChange=${() => setKind(value)} />
              <span class="wa-kind-card-text">
                <span class="wa-kind-card-title">${value === 'MARKETING' ? t('whatsappTemplates.campaignForm.kindMarketing') : t('whatsappTemplates.campaignForm.kindAnnouncement')}</span>
                <span class="wa-kind-card-help">${value === 'MARKETING' ? t('whatsappTemplates.campaignForm.kindMarketingHelp') : t('whatsappTemplates.campaignForm.kindAnnouncementHelp')}</span>
              </span>
            </label>`)}
          </div>
          <p class="wa-notice" data-tone=${kind === 'ANNOUNCEMENT' ? undefined : 'info'}>
            <${Icon} name="IconAlertTriangle" size=${16} /><span>${t('whatsappTemplates.campaignForm.reclassificationWarning')}</span>
          </p>
        </fieldset>
        <label class="form-field">
          <span class="form-field-label">${t('whatsappTemplates.campaignForm.header')}</span>
          <input class="text-input" value=${headerText} maxLength=${TEMPLATE_LIMITS.maximumHeaderLength + 20} autocomplete="off"
            onInput=${(/** @type {Event} */ event) => setHeaderText(/** @type {HTMLInputElement} */ (event.currentTarget).value)} onBlur=${touch('headerText')} />
        </label>
        <${FieldErrors} messages=${messagesFor('headerText')} />
        <div class="form-field">
          <label class="form-field-label" for="wa-template-body">${t('whatsappTemplates.campaignForm.body')}<span class="form-field-required">*</span></label>
          <textarea id="wa-template-body" ref=${bodyRef} class="text-input integration-textarea" value=${bodyText} rows=${7} maxLength=${TEMPLATE_LIMITS.maximumBodyLength + 50}
            aria-invalid=${messagesFor('bodyText').length > 0 ? 'true' : undefined} aria-describedby="wa-template-body-counter"
            onInput=${(/** @type {Event} */ event) => setBodyText(/** @type {HTMLTextAreaElement} */ (event.currentTarget).value)} onBlur=${touch('bodyText')}></textarea>
          <div class="wa-body-tools">
            <button ref=${variableButtonRef} type="button" class="button" data-variant="secondary" data-size="small" data-accent="default"
              aria-haspopup="menu" aria-expanded=${isVariableMenuOpen ? 'true' : 'false'} onClick=${() => setIsVariableMenuOpen(!isVariableMenuOpen)}>
              <${Icon} name="IconBraces" size=${14} /><span class="button-title">${t('whatsappTemplates.campaignForm.insertVariable')}</span>
            </button>
            <span id="wa-template-body-counter" class="wa-body-counter" data-over=${bodyLength > TEMPLATE_LIMITS.maximumBodyLength ? 'true' : undefined}>
              ${t('whatsappTemplates.campaignForm.bodyCounter', { count: bodyLength.toLocaleString('pt-BR') })}
            </span>
          </div>
          <${Dropdown} anchorRef=${variableButtonRef} isOpen=${isVariableMenuOpen} onClose=${() => setIsVariableMenuOpen(false)} width=${260} role="menu">
            <${DropdownMenuItems}>
              <${DropdownMenuSectionLabel} label=${t('whatsappTemplates.campaignForm.namedVariables')} />
              ${SUGGESTED_NAMED_VARIABLES.map((name) => html`<${MenuItem} key=${name} icon="IconBraces" disabled=${hasNumbered}
                text=${t(`whatsappTemplates.campaignForm.suggestedVariables.${name}`)} contextualText=${`{{${name}}}`}
                onClick=${() => insertToken(`{{${name}}}`, `whatsappTemplates.campaignForm.exampleValues.${name}`)} />`)}
              <${DropdownMenuSeparator} />
              <${MenuItem} icon="IconNumbers" disabled=${hasNamed} text=${t('whatsappTemplates.campaignForm.numberedVariable')} contextualText=${nextVariableToken(bodyText)}
                onClick=${() => insertToken(nextVariableToken(bodyText), null)} />
            <//>
          <//>
        </div>
        <${FieldErrors} messages=${messagesFor('bodyText')} />
        ${variables.length > 0
          ? html`<fieldset>
              <legend>${t('whatsappTemplates.campaignForm.examples')}</legend>
              ${variables.map((variable) => html`<label key=${variable} class="form-field">
                <span class="form-field-label">${t('whatsappTemplates.campaignForm.example', { variable })}<span class="form-field-required">*</span></span>
                <input class="text-input" value=${examples[variable] ?? ''} maxLength=${200} autocomplete="off"
                  onInput=${(/** @type {Event} */ event) => setExamples({ ...examples, [variable]: /** @type {HTMLInputElement} */ (event.currentTarget).value })}
                  onBlur=${touch('examples')} />
              </label>`)}
              <${FieldErrors} messages=${messagesFor('examples')} />
            </fieldset>`
          : null}
        <label class="form-field">
          <span class="form-field-label">${t('whatsappTemplates.campaignForm.footer')}</span>
          <input class="text-input" value=${footerText} maxLength=${TEMPLATE_LIMITS.maximumFooterLength + 20} autocomplete="off"
            onInput=${(/** @type {Event} */ event) => setFooterText(/** @type {HTMLInputElement} */ (event.currentTarget).value)} onBlur=${touch('footerText')} />
        </label>
        <${FieldErrors} messages=${messagesFor('footerText')} />
        ${kind === 'MARKETING'
          ? html`<label class="wa-check-row">
              <input type="checkbox" checked=${optOutButton} onChange=${(/** @type {Event} */ event) => setOptOutButton(/** @type {HTMLInputElement} */ (event.currentTarget).checked)} />
              <span class="wa-check-row-text">
                <span>${t('whatsappTemplates.campaignForm.optOutButton')}</span>
                <span class="wa-check-row-hint">${t('whatsappTemplates.campaignForm.optOutButtonHint')}</span>
              </span>
            </label>
            ${optOutButton
              ? html`<label class="form-field">
                  <span class="form-field-label">${t('whatsappTemplates.campaignForm.optOutButtonText')}</span>
                  <input class="text-input" value=${optOutButtonText} maxLength=${TEMPLATE_LIMITS.maximumButtonTextLength} autocomplete="off"
                    onInput=${(/** @type {Event} */ event) => setOptOutButtonText(/** @type {HTMLInputElement} */ (event.currentTarget).value)} onBlur=${touch('optOutButtonText')} />
                </label>
                <${FieldErrors} messages=${messagesFor('optOutButtonText')} />`
              : null}`
          : null}
        <${FormError} message=${serverError && !serverError.field ? serverError.message : null} />
        ${isSubmitAttempted && problems.length > 0 ? html`<p class="wa-field-error" role="status"><${Icon} name="IconAlertTriangle" size=${14} /><span>${t('whatsappTemplates.campaignForm.fixErrors')}</span></p>` : null}
        <div class="wa-form-actions">
          <${Button} type="submit" title=${t('whatsappTemplates.campaignForm.submit')} icon="IconSend" accent="blue" isLoading=${isSaving} />
          <${Button} title=${t('whatsappTemplates.campaignForm.cancel')} variant="secondary" onClick=${() => navigate(backHref)} />
        </div>
      </form>
      <aside class="wa-template-page-aside">
        <${WhatsappMessagePreview}
          title=${t('whatsappTemplates.campaignForm.preview')}
          template=${{
            headerText, bodyText: bodyText || t('whatsappTemplates.form.bodyPlaceholder'), footerText,
            buttons: includesOptOut && optOutButtonText.trim() ? [{ type: 'QUICK_REPLY', text: optOutButtonText.trim() }] : [],
          }}
          values=${examples} />
      </aside>
    </div>
  <//>`;
}
